/**
 * <instruction-focus-view>
 *
 * Fullscreen sewing-step viewer with horizontal swipe / arrow-key
 * navigation. Triggered by `[data-si-focus-trigger]` buttons or by
 * `<instruction-resume-banner>` (CustomEvent
 * `instruction-focus:open`).
 *
 * The component extracts step content from the inline step list in
 * the document (`[data-si-step]`) at open time, so it stays in sync
 * with the editor / locale changes without server-side duplication.
 *
 * Persists the highest viewed step under
 *   sewsimple:progress:{handle}
 * so `<instruction-resume-banner>` can offer "resume where you left
 * off" on the next visit.
 */

import { lockBodyScroll, unlockBodyScroll, toInt } from "./utils.js";

const SELECTORS = {
  scope: ".sewing-instructions",
  step: "[data-si-step]",
  trigger: "[data-si-focus-trigger]",
  closeBtn: "[data-ifv-close]",
  prevBtn: "[data-ifv-prev]",
  nextBtn: "[data-ifv-next]",
  slidesTrack: "[data-ifv-track]",
  progressBar: "[data-ifv-progress]",
  progressLabel: "[data-ifv-progress-label]",
  dots: "[data-ifv-dots]",
};

const PROGRESS_KEY_PREFIX = "sewsimple:progress:";
const SWIPE_THRESHOLD = 60;

// Storage key is `sewsimple:progress:{handle}:{instructionId}`, so an
// article with multiple instructions tracks each one independently.
function progressKey(handle, instructionId) {
  return `${PROGRESS_KEY_PREFIX}${handle}:${instructionId}`;
}

function readProgress(handle, instructionId) {
  try {
    const raw = localStorage.getItem(progressKey(handle, instructionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.step !== "number") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeProgress(handle, instructionId, step, total) {
  try {
    const existing = readProgress(handle, instructionId);
    const highest = existing ? Math.max(existing.step, step) : step;
    if (existing && existing.step === highest && existing.total === total) return;
    localStorage.setItem(
      progressKey(handle, instructionId),
      JSON.stringify({
        instructionId,
        step: highest,
        total,
        updated: new Date().toISOString(),
      }),
    );
    document.dispatchEvent(
      new CustomEvent("instruction-progress:updated", {
        detail: { handle, instructionId, step: highest, total },
      }),
    );
  } catch (_) {
    /* localStorage might be disabled / quota exceeded; non-fatal */
  }
}

class InstructionFocusView extends HTMLElement {
  #slides = [];
  #currentIndex = 0;
  #currentInstructionId = 1;
  #savedScrollY = 0;
  #pointerStartX = 0;
  #pointerStartY = 0;
  #pointerDelta = 0;
  #pointerActive = false;
  #pointerIsHorizontal = false;
  #wakeLock = null;
  #boundTriggers = [];

  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.onCustomOpenRequest = this.onCustomOpenRequest.bind(this);
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[InstructionFocusView:init]", error);
    }
  }

  init() {
    this.#getDataAttributes();
    this.#renderShell();
    this.#bindEvents();
  }

  #getDataAttributes() {
    this.handle = this.dataset.ifvHandle || "";
    this.stepLabel = this.dataset.ifvStepLabel || "Schritt";
    this.tipLabel = this.dataset.ifvTipLabel || "Mein Tipp";
    this.closeLabel = this.dataset.ifvCloseLabel || "Schließen";
    this.prevLabel = this.dataset.ifvPrevLabel || "Zurück";
    this.nextLabel = this.dataset.ifvNextLabel || "Weiter";
    this.ofLabel = this.dataset.ifvOfLabel || "von";
  }

  #renderShell() {
    if (!document.getElementById("ifv-styles")) {
      const style = document.createElement("style");
      style.id = "ifv-styles";
      style.textContent = IFV_CSS;
      document.head.appendChild(style);
    }

    this.innerHTML = `
      <div class="ifv-overlay" data-ifv-overlay>
        <div class="ifv-shell" role="dialog" aria-modal="true" aria-label="${escapeAttr(this.stepLabel)}">
          <header class="ifv-header">
            <div class="ifv-progress-wrap" aria-hidden="true">
              <div class="ifv-progress">
                <div class="ifv-progress-fill" data-ifv-progress></div>
              </div>
            </div>
            <div class="ifv-progress-label" data-ifv-progress-label></div>
            <button type="button" class="ifv-close" data-ifv-close aria-label="${escapeAttr(this.closeLabel)}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <div class="ifv-stage" data-ifv-stage>
            <div class="ifv-track" data-ifv-track></div>
          </div>

          <nav class="ifv-nav" aria-label="${escapeAttr(this.stepLabel)}">
            <button type="button" class="ifv-nav-btn ifv-nav-btn--prev" data-ifv-prev aria-label="${escapeAttr(this.prevLabel)}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              <span class="ifv-nav-btn__label">${escapeHTML(this.prevLabel)}</span>
            </button>
            <div class="ifv-dots" data-ifv-dots aria-hidden="true"></div>
            <button type="button" class="ifv-nav-btn ifv-nav-btn--next" data-ifv-next aria-label="${escapeAttr(this.nextLabel)}">
              <span class="ifv-nav-btn__label">${escapeHTML(this.nextLabel)}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="9 6 15 12 9 18"/>
              </svg>
            </button>
          </nav>
        </div>
      </div>
    `;

    this.overlay = this.querySelector("[data-ifv-overlay]");
    this.track = this.querySelector(SELECTORS.slidesTrack);
    this.progressBar = this.querySelector(SELECTORS.progressBar);
    this.progressLabel = this.querySelector(SELECTORS.progressLabel);
    this.dots = this.querySelector(SELECTORS.dots);
    this.prevBtn = this.querySelector(SELECTORS.prevBtn);
    this.nextBtn = this.querySelector(SELECTORS.nextBtn);
    this.closeBtn = this.querySelector(SELECTORS.closeBtn);
  }

  #bindEvents() {
    // Trigger buttons inside the block. Click target carries the
    // dataset; the CustomEvent path is for programmatic opens
    // (e.g. resume banner). Don't merge them — MouseEvent.detail is
    // the click count (1 for normal clicks), so checking `event.detail`
    // truthiness silently steals the trigger flow.
    document.querySelectorAll(SELECTORS.trigger).forEach((trigger) => {
      const handler = () => this.#openFromTrigger(trigger);
      trigger.addEventListener("click", handler);
      this.#boundTriggers.push({ trigger, handler });
    });

    document.addEventListener("instruction-focus:open", this.onCustomOpenRequest);

    this.closeBtn.addEventListener("click", () => this.close());
    this.prevBtn.addEventListener("click", () => this.go(this.#currentIndex - 1));
    this.nextBtn.addEventListener("click", () => this.go(this.#currentIndex + 1));

    this.overlay.addEventListener("click", (event) => {
      if (event.target === this.overlay) this.close();
    });

    this.track.addEventListener("pointerdown", (e) => this.#onPointerDown(e));
    this.track.addEventListener("pointermove", (e) => this.#onPointerMove(e));
    this.track.addEventListener("pointerup", (e) => this.#onPointerUp(e));
    this.track.addEventListener("pointercancel", () => this.#cancelPointer());
  }

  #openFromTrigger(trigger) {
    const stepIndex = toInt(trigger?.dataset?.siFocusStep, 1);
    const instructionId = toInt(trigger?.dataset?.siFocusInstructionId, 1);
    this.open(stepIndex, instructionId);
  }

  onCustomOpenRequest(event) {
    const detail = event?.detail || {};
    this.open(toInt(detail.step, 1), toInt(detail.instructionId, 1));
  }

  open(stepIndex = 1, instructionId = 1) {
    this.#currentInstructionId = instructionId;
    this.#collectSlides(instructionId);
    if (this.#slides.length === 0) return;

    this.#savedScrollY = window.scrollY || window.pageYOffset || 0;

    if (this.hasAttribute("hidden")) this.removeAttribute("hidden");
    this.classList.add("is-open");
    lockBodyScroll();
    document.addEventListener("keydown", this.onKeydown);

    this.#renderDots();
    this.go(Math.max(0, Math.min(this.#slides.length - 1, stepIndex - 1)), false);

    void this.#requestWakeLock();
  }

  close() {
    this.classList.remove("is-open");
    this.setAttribute("hidden", "");
    unlockBodyScroll();
    document.removeEventListener("keydown", this.onKeydown);
    void this.#releaseWakeLock();

    // unlockBodyScroll() reads body.style.top to restore scroll. On
    // some browsers (notably iOS Safari WebView contexts) the restore
    // isn't reliable and the page snaps to 0. Re-apply our snapshot.
    const restore = this.#savedScrollY;
    requestAnimationFrame(() => {
      if (Math.abs((window.scrollY || 0) - restore) > 5) {
        window.scrollTo({ top: restore, left: 0, behavior: "instant" });
      }
    });
  }

  go(index, animate = true) {
    if (this.#slides.length === 0) return;
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    this.#currentIndex = clamped;

    this.track.style.transition = animate ? "transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)" : "none";
    this.track.style.transform = `translateX(-${clamped * 100}%)`;

    this.#updateProgress();
    this.#updateDots();
    this.#updateNavState();

    writeProgress(this.handle, this.#currentInstructionId, clamped + 1, this.#slides.length);
  }

  #collectSlides(instructionId) {
    const selector = instructionId
      ? `${SELECTORS.step}[data-si-instruction-id="${instructionId}"]`
      : SELECTORS.step;
    const steps = document.querySelectorAll(selector);
    if (steps.length === 0) {
      this.#slides = [];
      this.track.innerHTML = "";
      return;
    }

    const fragments = Array.from(steps).map((stepEl) => this.#extractSlide(stepEl));
    this.#slides = fragments;
    this.track.innerHTML = fragments.map((s) => this.#renderSlide(s)).join("");
  }

  #extractSlide(stepEl) {
    const numeralEl = stepEl.querySelector(".sewing-step__numeral");
    const headingEl = stepEl.querySelector(".sewing-step__heading");
    const textEl = stepEl.querySelector(".sewing-step__text");
    const tipBodyEl = stepEl.querySelector(".sewing-step__tip-body");
    const imageEl = stepEl.querySelector(".sewing-step__image img");

    return {
      numeral: numeralEl?.textContent?.trim() || "",
      heading: headingEl?.textContent?.trim() || "",
      textHTML: textEl?.innerHTML || "",
      tipHTML: tipBodyEl?.innerHTML || "",
      imageSrc: imageEl?.currentSrc || imageEl?.src || "",
      imageAlt: imageEl?.alt || "",
    };
  }

  #renderSlide(slide) {
    const tipBlock = slide.tipHTML
      ? `<aside class="ifv-slide__tip">
           <span class="ifv-slide__tip-icon" aria-hidden="true">!</span>
           <div>
             <p class="ifv-slide__tip-label">${escapeHTML(this.tipLabel)}</p>
             <div class="ifv-slide__tip-body">${slide.tipHTML}</div>
           </div>
         </aside>`
      : "";
    const imageBlock = slide.imageSrc
      ? `<figure class="ifv-slide__image"><img src="${escapeAttr(slide.imageSrc)}" alt="${escapeAttr(slide.imageAlt)}" loading="lazy"/></figure>`
      : "";
    const headingBlock = slide.heading
      ? `<h2 class="ifv-slide__heading">${escapeHTML(slide.heading)}</h2>`
      : "";
    return `
      <article class="ifv-slide">
        <div class="ifv-slide__inner">
          ${imageBlock}
          <div class="ifv-slide__numeral">${escapeHTML(slide.numeral)}</div>
          <div class="ifv-slide__eyebrow">${escapeHTML(this.stepLabel)}</div>
          ${headingBlock}
          <div class="ifv-slide__text">${slide.textHTML}</div>
          ${tipBlock}
        </div>
      </article>
    `;
  }

  #renderDots() {
    const count = this.#slides.length;
    // Dots are a visual nicety — useless at large counts (a 38-step
    // pattern would overflow the bar and the active dot scrolls out of
    // view). Fall back to the top progress bar + numeric label.
    const MAX_DOTS = 12;
    if (count <= 1 || count > MAX_DOTS) {
      this.dots.innerHTML = "";
      return;
    }
    const dotsHTML = this.#slides
      .map((_, i) => `<button type="button" class="ifv-dot" data-ifv-dot-index="${i}" aria-label="${escapeAttr(this.stepLabel)} ${i + 1}"></button>`)
      .join("");
    this.dots.innerHTML = dotsHTML;
    this.dots.querySelectorAll("[data-ifv-dot-index]").forEach((dot) => {
      dot.addEventListener("click", () => this.go(toInt(dot.dataset.ifvDotIndex, 0)));
    });
  }

  #updateDots() {
    if (!this.dots.firstChild) return;
    this.dots.querySelectorAll(".ifv-dot").forEach((dot, i) => {
      dot.classList.toggle("is-active", i === this.#currentIndex);
    });
  }

  #updateProgress() {
    const total = this.#slides.length;
    const current = this.#currentIndex + 1;
    const pct = total > 0 ? (current / total) * 100 : 0;
    this.progressBar.style.width = `${pct}%`;
    this.progressLabel.textContent = `${this.stepLabel} ${current} ${this.ofLabel} ${total}`;
  }

  #updateNavState() {
    this.prevBtn.disabled = this.#currentIndex === 0;
    this.nextBtn.disabled = this.#currentIndex === this.#slides.length - 1;
  }

  #onPointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    this.#pointerActive = true;
    this.#pointerIsHorizontal = false;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.#pointerDelta = 0;
    this.track.style.transition = "none";
  }

  #onPointerMove(event) {
    if (!this.#pointerActive) return;
    const dx = event.clientX - this.#pointerStartX;
    const dy = event.clientY - this.#pointerStartY;

    // Direction is decided on the first significant move and locked
    // in. iOS Safari sometimes fires noisy initial deltas; an 8px
    // threshold keeps tap-targets responsive.
    if (!this.#pointerIsHorizontal) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical scroll — yield to the browser.
        this.#cancelPointer();
        return;
      }
      this.#pointerIsHorizontal = true;
      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }

    this.#pointerDelta = dx;
    const offsetPct = (dx / this.track.clientWidth) * 100;
    this.track.style.transform = `translateX(calc(-${this.#currentIndex * 100}% + ${offsetPct}%))`;
  }

  #onPointerUp() {
    if (!this.#pointerActive) return;
    const wasHorizontal = this.#pointerIsHorizontal;
    const delta = this.#pointerDelta;
    this.#pointerActive = false;
    this.#pointerIsHorizontal = false;
    this.#pointerDelta = 0;

    if (!wasHorizontal) return; // never crossed the threshold — treat as tap

    if (delta < -SWIPE_THRESHOLD) {
      this.go(this.#currentIndex + 1);
    } else if (delta > SWIPE_THRESHOLD) {
      this.go(this.#currentIndex - 1);
    } else {
      this.go(this.#currentIndex);
    }
  }

  #cancelPointer() {
    this.#pointerActive = false;
    this.#pointerIsHorizontal = false;
    this.#pointerDelta = 0;
    this.go(this.#currentIndex);
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.go(this.#currentIndex + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.go(this.#currentIndex - 1);
    }
  }

  async #requestWakeLock() {
    if (!("wakeLock" in navigator)) return;
    try {
      this.#wakeLock = await navigator.wakeLock.request("screen");
    } catch (_) {
      /* user gesture / permissions denied — non-fatal */
    }
  }

  async #releaseWakeLock() {
    if (!this.#wakeLock) return;
    try {
      await this.#wakeLock.release();
    } catch (_) {
      /* ignore */
    }
    this.#wakeLock = null;
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("instruction-focus:open", this.onCustomOpenRequest);
    this.#boundTriggers.forEach(({ trigger, handler }) => {
      trigger.removeEventListener("click", handler);
    });
    this.#boundTriggers = [];
    void this.#releaseWakeLock();
  }
}

function escapeHTML(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(str) {
  return escapeHTML(str).replace(/"/g, "&quot;");
}

const IFV_CSS = `
.ifv-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: color-mix(in srgb, #1a1a1a 90%, transparent);
  backdrop-filter: blur(4px);
  display: flex; align-items: stretch; justify-content: stretch;
  animation: ifv-fade 0.18s ease;
}
@keyframes ifv-fade { from { opacity: 0; } to { opacity: 1; } }

.ifv-shell {
  background: #fff;
  color: #1a1a1a;
  width: 100%; height: 100%;
  display: grid; grid-template-rows: auto 1fr auto;
  max-width: 720px; margin: auto;
}
@media (min-width: 768px) {
  .ifv-shell { max-height: min(92vh, 900px); border-radius: 18px; overflow: hidden; }
  .ifv-overlay { padding: 1rem; }
}

.ifv-header {
  display: grid; grid-template-columns: 1fr auto auto; align-items: center;
  gap: 0.75rem; padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid color-mix(in srgb, currentColor 8%, transparent);
}
.ifv-progress-wrap { width: 100%; }
.ifv-progress {
  width: 100%; height: 6px; border-radius: 999px;
  background: color-mix(in srgb, currentColor 8%, transparent);
  overflow: hidden;
}
.ifv-progress-fill {
  height: 100%; width: 0%;
  background: var(--color-accent--primary, #c1573a);
  transition: width 0.3s ease;
  border-radius: 999px;
}
.ifv-progress-label {
  font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em;
  white-space: nowrap; opacity: 0.75;
  font-variant-numeric: tabular-nums;
}
.ifv-close {
  background: transparent; border: 0; padding: 0.45rem;
  border-radius: 999px; cursor: pointer; color: currentColor;
  width: 36px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
}
.ifv-close:hover { background: color-mix(in srgb, currentColor 8%, transparent); }
.ifv-close svg { width: 20px; height: 20px; }

.ifv-stage {
  overflow: hidden;
  position: relative;
}
.ifv-track {
  display: flex; height: 100%;
  transform: translateX(0);
  will-change: transform;
  /* Browser darf vertikal scrollen / pinchen, horizontal bleibt für
     den JS-Swipe-Handler. Ohne diese Deklaration verschluckt iOS
     Safari die Pointer-Events bei horizontalen Gesten. */
  touch-action: pan-y;
}
.ifv-slide {
  flex: 0 0 100%;
  overflow-y: auto;
  padding: 1.25rem 1.25rem 1.5rem;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}
.ifv-slide__inner {
  max-width: 38rem; margin: 0 auto;
  display: flex; flex-direction: column; gap: 0.6rem;
}
.ifv-slide__image {
  background: color-mix(in srgb, var(--color-accent--primary, #c1573a) 18%, #fff);
  border-radius: 14px; overflow: hidden; margin: 0 0 0.5rem;
}
.ifv-slide__image img {
  width: 100%; height: auto; display: block;
  max-height: 50vh; object-fit: contain; background: #f6f5f3;
}
.ifv-slide__numeral {
  font-family: var(--font-heading-family, serif);
  font-size: 2.4rem; line-height: 1; font-weight: 600;
  color: var(--color-accent--primary, #c1573a);
  font-variant-numeric: tabular-nums;
}
.ifv-slide__eyebrow {
  font-size: 0.7rem; letter-spacing: 0.22em; font-weight: 600;
  text-transform: uppercase; opacity: 0.6;
}
.ifv-slide__heading {
  font-family: var(--font-heading-family, serif);
  font-size: 1.4rem; line-height: 1.25; font-weight: 600;
  margin: 0.25rem 0 0.4rem;
}
.ifv-slide__text :where(p) { margin-bottom: 0.55rem; }
.ifv-slide__text :where(ul, ol) { margin: 0.4rem 0 0.6rem 1.1rem; padding-left: 0.25rem; }
.ifv-slide__text :where(li) { margin-bottom: 0.15rem; }

.ifv-slide__tip {
  margin-top: 0.9rem;
  border-radius: 12px;
  background: color-mix(in srgb, var(--color-accent--primary, #c1573a) 14%, #fff);
  padding: 0.85rem 1rem;
  display: flex; gap: 0.75rem; align-items: flex-start;
}
.ifv-slide__tip-icon {
  flex-shrink: 0; width: 22px; height: 22px;
  border-radius: 999px;
  background: var(--color-accent--primary, #c1573a);
  color: #fff; display: inline-flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.85rem;
}
.ifv-slide__tip-label { font-weight: 700; margin: 0 0 0.2rem; font-size: 0.9rem; }
.ifv-slide__tip-body :where(p) { margin: 0; font-size: 0.92rem; line-height: 1.5; }

.ifv-nav {
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center;
  gap: 0.75rem; padding: 0.75rem 1rem 1rem;
  border-top: 1px solid color-mix(in srgb, currentColor 8%, transparent);
}
.ifv-nav-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  border: 0; background: transparent;
  font-weight: 600; font-size: 0.9rem; cursor: pointer;
  padding: 0.5rem 0.75rem; border-radius: 999px;
  color: currentColor;
}
.ifv-nav-btn--prev { justify-self: start; }
.ifv-nav-btn--next { justify-self: end; }
.ifv-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.ifv-nav-btn:not(:disabled):hover {
  background: color-mix(in srgb, currentColor 7%, transparent);
}
.ifv-nav-btn svg { width: 18px; height: 18px; }
.ifv-nav-btn__label { font-size: 0.88rem; }
@media (max-width: 420px) {
  .ifv-nav-btn__label { display: none; }
}

.ifv-dots {
  display: inline-flex; gap: 0.35rem; align-items: center;
  max-width: 50vw; overflow-x: auto; scrollbar-width: none;
  padding: 0.25rem;
}
.ifv-dots::-webkit-scrollbar { display: none; }
.ifv-dot {
  width: 8px; height: 8px; border-radius: 999px;
  background: color-mix(in srgb, currentColor 18%, transparent);
  border: 0; padding: 0; cursor: pointer;
  flex-shrink: 0; transition: all 0.2s ease;
}
.ifv-dot.is-active {
  width: 22px;
  background: var(--color-accent--primary, #c1573a);
}

instruction-focus-view[hidden] { display: none !important; }
`;

if (!customElements.get("instruction-focus-view")) {
  customElements.define("instruction-focus-view", InstructionFocusView);
}
